import { TFunction } from "i18next";
import { QueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { Design } from "../../core/models/entities/Design";
import { Role } from "../../core/models/entities/Role";
import { PagedResponse } from "../../data/models/responses/pagedResponse";
import { VerifyStorageRowDto } from "../../data/models/responses/VerifyStorageResponseDto";
import { isLogoDetailQueryKey } from "../../data/repositories/LogoRepository";
import {
  freshCountsFromRows,
  FreshStorageCounts,
} from "../../presentation/screens/order-confirmation/storageVerify";
import { parseUtcStamp, storageAgeLabel } from "./storageUpdatedAt";

// Stock freshness on view (flag storageFreshnessEnabled, Nichlas 7/9: "the
// count on the front page must be correct"). Pure helpers behind
// useStorageFreshness: which of the logos on screen carry a storage stamp
// that is missing, never set or older than the max age; the module-level
// "already asked" guard that keeps the same logo from being requested twice
// within the API's dedupe window no matter how often the page re-renders,
// scrolls, filters or navigates list <-> detail; and the cache patch that
// lands the API's fresh counts and stamps in every react-query entry of the
// act-as scope the logo sits in (paged list, infinite list, detail).

/** Default staleness threshold; the API's StorageFreshnessMaxAgeHours wins. */
export const STORAGE_FRESHNESS_MAX_AGE_HOURS = 24;
/** Rapid page/filter switches collapse into ONE call with the final set. */
export const STORAGE_FRESHNESS_DEBOUNCE_MS = 1000;
/** Hard cap on how long a call may take; a late answer is dropped. */
export const STORAGE_FRESHNESS_TIMEOUT_MS = 8000;
/** A logo asked for is not asked again within this window (= API dedupe). */
export const STORAGE_FRESHNESS_REQUEST_TTL_MS = 5 * 60_000;
/** Never more ids per call than the API's cap (CheckoutStorageVerifyMaxLogos). */
export const STORAGE_FRESHNESS_MAX_IDS = 50;
/**
 * Manual "refresh stock now": after an answer that could not verify the row
 * (budget spent, NAV degraded, transport error) every button pauses this
 * long - a second click would only meet the API's 90 s failed marker.
 */
export const STORAGE_MANUAL_BACKOFF_MS = 60_000;
/**
 * A row the API answered a forced click from its window for (stamped less
 * than 5 minutes ago on ITS clock) may read a little older on this machine
 * - clock skew - so a stamp inside the window plus this margin still reads
 * as "just updated", never as a failure. A row that really could not be
 * verified keeps a stamp hours or days old; the margin cannot mistake one
 * for the other.
 */
export const STORAGE_COOLDOWN_SKEW_MS = 60_000;

/**
 * Milliseconds of a storage stamp; undefined for a missing, unset
 * (0001-01-01 -> ms <= 0) or unparsable one. Same parser as the age line
 * (parseUtcStamp: zoneless = UTC), so window and label agree on every stamp.
 */
export function storageStampMs(
  iso: string | null | undefined
): number | undefined {
  if (!iso) return undefined;
  const ms = parseUtcStamp(iso);
  return Number.isNaN(ms) || ms <= 0 ? undefined : ms;
}

/**
 * The API's manual guard (StorageFreshnessPolicy.NeedsForcedRefresh, negated)
 * mirrored: a row stamped less than the dedupe window ago - or in the future,
 * clock skew - is answered from the row, never from NAV, whoever stamped it,
 * in whatever tab, before or after a reload. The button of such a row is
 * locked for exactly as long as the API would refuse it. `marginMs` widens
 * the window for reading an ANSWER (STORAGE_COOLDOWN_SKEW_MS); the button
 * itself uses the bare window.
 */
export function isInsideManualWindow(
  iso: string | null | undefined,
  nowMs: number,
  marginMs: number = 0
): boolean {
  const ms = storageStampMs(iso);
  return ms != null && nowMs - ms < STORAGE_FRESHNESS_REQUEST_TTL_MS + marginMs;
}

const MANUAL_WINDOW_MINUTES = STORAGE_FRESHNESS_REQUEST_TTL_MS / 60_000;

/**
 * Whole minutes until the window of a stamp closes, for "you can refresh
 * again in {n} min": rounded up, never below 1 (the caller already knows the
 * row is inside the window - a window that closed on this clock but not on
 * the API's is "1 min"), never above the window itself (a stamp in the
 * future).
 */
export function manualWindowMinutesLeft(
  iso: string | null | undefined,
  nowMs: number
): number {
  const ms = storageStampMs(iso);
  if (ms == null) return 1;
  const left = STORAGE_FRESHNESS_REQUEST_TTL_MS - (nowMs - ms);
  return Math.min(
    MANUAL_WINDOW_MINUTES,
    Math.max(1, Math.ceil(left / 60_000))
  );
}

/**
 * The interpolation of the "updated {relative} - again in {n} min" texts
 * (tooltip and toast): the stamp's age as the age line shows it ("just now",
 * "3 min ago") and the minutes left of its window.
 */
export function manualWindowParams(
  iso: string | null | undefined,
  nowMs: number,
  t: TFunction
): { relative: string; n: number } {
  return {
    relative: storageAgeLabel(iso, t, nowMs),
    n: manualWindowMinutesLeft(iso, nowMs),
  };
}

/**
 * The API's rule (StorageFreshnessPolicy.IsStale) mirrored: no stamp, an
 * unparsable or unset stamp (0001-01-01 -> ms <= 0), or one older than
 * maxAgeHours is stale. A stamp in the future (clock skew) is fresh. The
 * stamp is read with parseUtcStamp (zoneless = UTC), the same parser the
 * "Stock updated ..." label uses, so filter and label agree on every stamp.
 */
export function isStorageStale(
  iso: string | null | undefined,
  nowMs: number,
  maxAgeHours: number = STORAGE_FRESHNESS_MAX_AGE_HOURS
): boolean {
  if (!iso) return true;
  const ms = parseUtcStamp(iso);
  if (Number.isNaN(ms) || ms <= 0) return true;
  return nowMs - ms > maxAgeHours * 3_600_000;
}

/**
 * Module-level "already asked" guard, keyed by (organization, logo id) with a
 * timestamp - a storage row is per organization, so the same logo asked for
 * as vendor A must not be blocked for vendor B (the API dedupes per org too).
 * A plain module map (not a ref) so it survives StrictMode's remount, the
 * list -> detail -> list navigation and any number of components showing the
 * same logo: one request per (org, logo) per TTL, period.
 */
export interface RequestedStore {
  has(orgId: number, id: number, nowMs: number): boolean;
  mark(orgId: number, ids: readonly number[], nowMs: number): void;
  reset(): void;
  /**
   * Changes on every mark/reset; with `subscribe` this is the
   * useSyncExternalStore snapshot a "refresh stock now" button reads, so it
   * locks the moment the on-view call asks for its logo - whoever asked.
   */
  version(): number;
  subscribe(listener: () => void): () => void;
}

export function createRequestedStore(
  ttlMs: number = STORAGE_FRESHNESS_REQUEST_TTL_MS
): RequestedStore {
  const asked = new Map<string, number>();
  const listeners = new Set<() => void>();
  let version = 0;
  const keyOf = (orgId: number, id: number) => `${orgId}:${id}`;
  const notify = () => {
    version += 1;
    listeners.forEach((listener) => listener());
  };
  return {
    has(orgId, id, nowMs) {
      const key = keyOf(orgId, id);
      const at = asked.get(key);
      if (at == null) return false;
      if (nowMs - at >= ttlMs) {
        asked.delete(key);
        return false;
      }
      return true;
    },
    mark(orgId, ids, nowMs) {
      asked.forEach((at, key) => {
        if (nowMs - at >= ttlMs) asked.delete(key);
      });
      ids.forEach((id) => asked.set(keyOf(orgId, id), nowMs));
      notify();
    },
    reset() {
      asked.clear();
      notify();
    },
    version() {
      return version;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const requestedStore: RequestedStore = createRequestedStore();

export function resetRequestedStoreForTests(): void {
  requestedStore.reset();
}

export type ManualRefreshState =
  | "idle"
  | "loading"
  | "busy"
  | "cooldown"
  | "unavailable"
  | "failed";

/**
 * A page-wide pause of every "refresh stock now" button until `untilMs`:
 * "unavailable" when the API ignored `force` (its manual flag off) or
 * answered enabled=false - 5 minutes, one probe per dedupe window;
 * "failed" when a click could not verify its row (budget spent, NAV
 * degraded, failed marker) or the transport failed - 60 s, a second click
 * would only meet the API's 90 s failed marker. untilMs 0 = no pause.
 */
export interface ManualRefreshLock {
  untilMs: number;
  kind: "unavailable" | "failed";
}

export const NO_MANUAL_REFRESH_LOCK: ManualRefreshLock = {
  untilMs: 0,
  kind: "failed",
};

/** Key of a (org, logo) pair in the in-flight set of the manual refresh. */
export function manualRefreshKey(orgId: number, logoId: number): string {
  return `${orgId}:${logoId}`;
}

/**
 * What the "refresh stock now" button of one logo may do right now:
 * "loading" while its own call is in the air; the pause's kind while every
 * button is paused (see ManualRefreshLock); "busy" while ANOTHER logo's
 * click is in the air - one manual call per page at a time, like the
 * on-view call, so ten quick clicks never become ten parallel NAV look-ups
 * against app117; "cooldown" while the ROW's stamp is inside the API's
 * manual window (isInsideManualWindow: stamped less than 5 minutes ago,
 * whoever stamped it - this tab, another tab, a reload ago, the on-view
 * call, a colleague - the API would answer from the row, not from NAV) or
 * while the logo was asked for within the TTL by this button or by the
 * on-view call and no stamp landed yet (the call is in the air, or did not
 * verify the row); otherwise "idle". Pure: same inputs, same answer, no
 * clock of its own.
 */
export function manualRefreshState(
  orgId: number,
  logoId: number,
  nowMs: number,
  requested: RequestedStore,
  inFlight: ReadonlySet<string>,
  lock: ManualRefreshLock,
  storageUpdatedAtUtc?: string | null
): ManualRefreshState {
  if (inFlight.has(manualRefreshKey(orgId, logoId))) return "loading";
  if (nowMs < lock.untilMs) return lock.kind;
  if (inFlight.size > 0) return "busy";
  if (isInsideManualWindow(storageUpdatedAtUtc, nowMs)) return "cooldown";
  if (requested.has(orgId, logoId, nowMs)) return "cooldown";
  return "idle";
}

/**
 * The row of `logoId` in a forced answer the API gave from its window
 * (StorageFreshnessPolicy.NeedsForcedRefresh false): not verified in THIS
 * call, but stamped from NAV inside the window - by another tab's click,
 * the on-view call, a colleague, or this tab before a reload. The figure it
 * holds IS the verified one of a moment ago, and the answer is a cooldown,
 * not a failure. Undefined for a verified row, a missing row, or a row whose
 * stamp is older than the window (plus the skew margin) - THAT is a row the
 * API could not verify.
 */
export function rowInsideManualWindow(
  rows: readonly VerifyStorageRowDto[] | undefined,
  logoId: number,
  nowMs: number
): VerifyStorageRowDto | undefined {
  const row = rows?.find((r) => r.logoId === logoId);
  if (!row || row.verified) return undefined;
  return isInsideManualWindow(
    row.storageUpdatedAtUtc,
    nowMs,
    STORAGE_COOLDOWN_SKEW_MS
  )
    ? row
    : undefined;
}

/**
 * The i18n key of the button's tooltip (and accessible name) in `state`.
 * "cooldown" is honest about what happened: "Updated {relative} - you can
 * refresh again in {n} min" only when the logo's stamp is inside the manual
 * window (the refresh, manual or on-view, landed); a stamp older than that,
 * missing or unset means the asked-for refresh did not verify the row - or
 * is still in the air - so "Could not refresh yet - try again in a few
 * minutes", never a claim of freshness the age line below contradicts.
 */
export function manualRefreshTitleKey(
  state: ManualRefreshState,
  storageUpdatedAtUtc: string | null | undefined,
  nowMs: number
): string {
  switch (state) {
    case "cooldown":
      return isInsideManualWindow(storageUpdatedAtUtc, nowMs)
        ? "compLogoDesign.refreshStockCooldown"
        : "compLogoDesign.refreshStockRetryLater";
    case "unavailable":
      return "compLogoDesign.refreshStockUnavailable";
    case "failed":
      return "compLogoDesign.refreshStockFailed";
    default:
      return "compLogoDesign.refreshStockNow";
  }
}

/**
 * The button's tooltip text in `state`: the key of manualRefreshTitleKey,
 * translated - with the stamp's age and the minutes left of its window
 * interpolated when it is the "updated {relative} - again in {n} min" one.
 */
export function manualRefreshTitle(
  state: ManualRefreshState,
  storageUpdatedAtUtc: string | null | undefined,
  nowMs: number,
  t: TFunction
): string {
  const key = manualRefreshTitleKey(state, storageUpdatedAtUtc, nowMs);
  return key === "compLogoDesign.refreshStockCooldown"
    ? t(key, manualWindowParams(storageUpdatedAtUtc, nowMs, t))
    : t(key);
}

/**
 * Distinct ids (> 0) of the logos whose storage stamp is stale and that have
 * not been asked for within the TTL for this organization, in first-seen
 * order.
 */
export function selectStaleLogoIds(
  orgId: number,
  logos: readonly BaseLogo[] | undefined,
  nowMs: number,
  maxAgeHours: number,
  requested: RequestedStore
): number[] {
  if (!logos || logos.length === 0) return [];
  const seen = new Set<number>();
  const ids: number[] = [];
  logos.forEach((logo) => {
    const id = logo?.id;
    if (id == null || !(id > 0) || seen.has(id)) return;
    seen.add(id);
    if (!isStorageStale(logo.storageUpdatedAtUtc, nowMs, maxAgeHours)) return;
    if (requested.has(orgId, id, nowMs)) return;
    ids.push(id);
  });
  return ids;
}

/**
 * Stable signature of the ids a page shows, so an effect can key on "which
 * logos" instead of the array's identity (a cache patch hands the list a new
 * results array with the same logos - that must not re-trigger anything).
 */
export function logoIdsSignature(
  logos: readonly BaseLogo[] | undefined
): string {
  if (!logos || logos.length === 0) return "";
  return logos
    .map((logo) => logo?.id)
    .filter((id): id is number => id != null && id > 0)
    .join(",");
}

/**
 * The organization whose storage row the logo list / detail shows - the same
 * resolution GetAllLogos applies server-side: an admin acting as a vendor
 * sees that vendor's rows; an admin on their own sees org 0 (the freshest
 * row across orgs - nothing to refresh, so the hook idles); everyone else
 * their own organization. 0 = idle.
 */
export function resolveStorageFreshnessOrgId(
  actAsOrgId: number | undefined,
  role: Role | undefined,
  organizationId: number | undefined
): number {
  if (actAsOrgId != null && actAsOrgId > 0) return actAsOrgId;
  if (role === Role.Admin) return 0;
  return organizationId != null && organizationId > 0 ? organizationId : 0;
}

function sameDigits(
  current: BaseLogo["numberLogosInStorage"] | undefined,
  next: BaseLogo["numberLogosInStorage"]
): boolean {
  if (!current || current.length !== next.length) return false;
  return next.every((d) => {
    const c = current.find((x) => x.value === d.value);
    return !!c && c.inStorage === d.inStorage;
  });
}

/**
 * The logo with the verified count, digits (number logos, only when
 * delivered) and stamp from `fresh` - or the very same object when it has
 * nothing for this logo or nothing differs, so callers can detect "unchanged"
 * with a reference check (same rule as patchStorageCounts in the cart).
 */
export function patchLogoWithRows<T extends BaseLogo>(
  logo: T,
  fresh: FreshStorageCounts
): T {
  if (!logo || !fresh.scalar.has(logo.id)) return logo;
  const nextCount = fresh.scalar.get(logo.id)!;
  const nextDigits = fresh.digits.get(logo.id);
  const nextStamp = fresh.updatedAt.get(logo.id) ?? null;
  const countSame = logo.inStorage === nextCount;
  const digitsSame =
    !nextDigits || sameDigits(logo.numberLogosInStorage, nextDigits);
  const stampSame = (logo.storageUpdatedAtUtc ?? null) === nextStamp;
  if (countSame && digitsSame && stampSame) return logo;
  return {
    ...logo,
    inStorage: nextCount,
    numberLogosInStorage: nextDigits ?? logo.numberLogosInStorage,
    storageUpdatedAtUtc: nextStamp,
  };
}

function patchResults(
  results: BaseLogo[] | undefined,
  fresh: FreshStorageCounts
): BaseLogo[] | undefined {
  if (!Array.isArray(results)) return results;
  let changed = false;
  const next = results.map((logo) => {
    const patched = patchLogoWithRows(logo, fresh);
    if (patched !== logo) changed = true;
    return patched;
  });
  return changed ? next : results;
}

function isPagedLogos(data: unknown): data is PagedResponse<BaseLogo> {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as PagedResponse<BaseLogo>).results)
  );
}

function isInfiniteLogos(
  data: unknown
): data is { pages: PagedResponse<BaseLogo>[] } {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

/**
 * A cached ["logos", ...] entry with the fresh counts applied: a paged list
 * ({results}), an infinite list ({pages: [{results}]}) or - when `isDetail`
 * - a bare BaseLogo. Any other shape (or nothing to change) comes back as
 * the very same reference.
 */
export function patchCachedLogosData(
  data: unknown,
  fresh: FreshStorageCounts,
  isDetail: boolean
): unknown {
  if (!data) return data;
  if (isDetail) {
    const logo = data as BaseLogo;
    return typeof logo.id === "number" ? patchLogoWithRows(logo, fresh) : data;
  }
  if (isPagedLogos(data)) {
    const results = patchResults(data.results, fresh);
    return results === data.results ? data : { ...data, results };
  }
  if (isInfiniteLogos(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      if (!isPagedLogos(page)) return page;
      const results = patchResults(page.results, fresh);
      if (results === page.results) return page;
      changed = true;
      return { ...page, results };
    });
    return changed ? { ...data, pages } : data;
  }
  return data;
}

/**
 * The act-as segment of a ["logos", ...] query key - the scope whose storage
 * rows the entry holds: ["logos", actAs, id] (detail, the shared
 * isLogoDetailQueryKey), ["logos", actAs, typeFilter, qs] (paged list) and
 * ["logos", "inf", actAs, qs] (infinite list). Undefined for every other form
 * - the design picker's ["logos", customerId, "inf", qs] carries no act-as
 * segment, so which organization's rows it holds cannot be told from the key.
 */
export function logoQueryKeyScope(key: readonly unknown[]): number | undefined {
  if (key[0] !== "logos") return undefined;
  if (isLogoDetailQueryKey(key)) return key[1] as number;
  if (key.length !== 4) return undefined;
  if (key[1] === "inf") {
    return typeof key[2] === "number" ? key[2] : undefined;
  }
  if (typeof key[1] === "number" && key[2] !== "inf") return key[1];
  return undefined;
}

/**
 * Lands the verified rows in every react-query entry under the ["logos"]
 * prefix that belongs to `scope` (the act-as segment the rows were fetched
 * for - actAsCacheKey() at call time, the same rule as the checkout verify's
 * syncStorageQueryCache): the paged lists (the logo list screen), the
 * infinite lists (design editor) and the detail entries. A storage row is per
 * organization and nothing clears the cache on an identity switch, so an
 * entry of another scope - a previous act-as vendor's list, still cached -
 * is never written; neither is the design picker's unscoped entry. Entries
 * that would not change are not written at all - no dispatch, no re-render;
 * absent entries stay absent. Returns the number of entries written.
 */
export function patchStorageInQueryCache(
  queryClient: QueryClient,
  rows: VerifyStorageRowDto[],
  scope: number
): number {
  const fresh = freshCountsFromRows(rows);
  if (fresh.scalar.size === 0) return 0;
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["logos"] })
    .forEach((query) => {
      if (logoQueryKeyScope(query.queryKey) !== scope) return;
      const current = query.state.data;
      if (current == null) return;
      const next = patchCachedLogosData(
        current,
        fresh,
        isLogoDetailQueryKey(query.queryKey)
      );
      if (next === current) return;
      queryClient.setQueryData(query.queryKey, next);
      written += 1;
    });
  return written;
}

/**
 * A design detail entry: ["designs", actAs, id] (DesignRepository.useDesign,
 * the very key useCartSync reads for a design in the cart). The design lists
 * (["designs", actAs, qs]) carry a string in the last segment.
 */
export function isDesignDetailQueryKey(key: readonly unknown[]): boolean {
  return (
    key[0] === "designs" &&
    key.length === 3 &&
    typeof key[1] === "number" &&
    typeof key[2] === "number"
  );
}

/**
 * The design with the verified counts applied to its own logo copies
 * (designLogos[].logo) - or the very same object when nothing differs.
 */
export function patchDesignWithRows(
  design: Design,
  fresh: FreshStorageCounts
): Design {
  if (!design || !Array.isArray(design.designLogos)) return design;
  let changed = false;
  const designLogos = design.designLogos.map((dl) => {
    if (!dl?.logo) return dl;
    const logo = patchLogoWithRows(dl.logo, fresh);
    if (logo === dl.logo) return dl;
    changed = true;
    return { ...dl, logo };
  });
  return changed ? { ...design, designLogos } : design;
}

/**
 * Lands the verified rows in the design DETAIL entries of `scope`
 * (["designs", actAs, id]) - the copies useCartSync compares a cart design's
 * logos against: with the cart patched but this entry not, the sync would
 * read the cart as stale and write the old count straight back (the reason
 * handleApplyVerifiedStorage patches it too). Same rules as
 * patchStorageInQueryCache: another scope's entries and the lists are never
 * written, unchanged entries are not written, absent entries stay absent.
 * Returns the number of entries written.
 */
export function patchStorageInDesignQueryCache(
  queryClient: QueryClient,
  rows: VerifyStorageRowDto[],
  scope: number
): number {
  const fresh = freshCountsFromRows(rows);
  if (fresh.scalar.size === 0) return 0;
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["designs"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (!isDesignDetailQueryKey(key) || key[1] !== scope) return;
      const current = query.state.data as Design | undefined;
      if (current == null) return;
      const next = patchDesignWithRows(current, fresh);
      if (next === current) return;
      queryClient.setQueryData(key, next);
      written += 1;
    });
  return written;
}
